import React, { useEffect, useState } from "react";
import { ShieldAlert, X } from "lucide-react";
import { epsteinSiteNotice } from "../lib/api";

/**
 * ArchiveStatusNotice — full-screen Archive entry modal.
 *
 * Behaviour:
 *   • Calls `/api/site/notice` on mount. If `archive_status_notice_enabled`
 *     is false → component renders nothing.
 *   • Otherwise, intercepts the user with a full-screen dark Archive-style
 *     notice that frames the site as a living research platform under
 *     active editorial development.
 *   • Dismissal is persisted in localStorage for 7 days. Re-enabling the
 *     toggle in admin will *not* re-trigger the modal for users who have
 *     already acknowledged within the 7-day window — by design, the modal
 *     is a soft entry banner, not a blocking gate.
 *
 * Visual: matte black backdrop, amber/red accents, monospaced banner,
 * intentionally aligned with the Black-Ops / Cinematic Intelligence
 * aesthetic of the rest of The Archive.
 */
const STORAGE_KEY = "archive_status_notice_v1_ack_at";
const ACK_WINDOW_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

const isWithinAckWindow = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < ACK_WINDOW_MS;
  } catch {
    return false;
  }
};

export const ArchiveStatusNotice = () => {
  const [enabled, setEnabled] = useState(false);
  const [mode, setMode] = useState("active_development");
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const r = await epsteinSiteNotice();
        if (!active) return;
        const on = !!r?.archive_status_notice_enabled;
        setEnabled(on);
        setMode(r?.archive_status_notice_mode || "active_development");
        if (on && !isWithinAckWindow()) {
          setDismissed(false);
        } else if (!on) {
          // Toggle was switched off server-side — re-suppress immediately.
          setDismissed(true);
        }
      } catch {
        // soft-fail — the modal is non-critical chrome
      }
    })();
    return () => { active = false; };
  }, []);

  // Lock the underlying page scroll while the modal is open.
  useEffect(() => {
    if (!enabled || dismissed) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [enabled, dismissed]);

  const acknowledge = () => {
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {
      // localStorage may be unavailable in private mode — that's OK,
      // the modal will just re-appear on next visit.
    }
    setDismissed(true);
  };

  if (!enabled || dismissed) return null;
  // Never block admin or legal/editorial pages.
  if (typeof window !== "undefined") {
    const p = window.location.pathname || "";
    if (
      p.startsWith("/admin")
      || p.startsWith("/auth")
      || p.startsWith("/legal")
      || p.startsWith("/editorial-standards")
      || p.startsWith("/unsubscribe")
    ) return null;
  }

  const MODE_META = {
    active_development: { label: "ACTIVE DEVELOPMENT", color: "#D92D20" },
    beta_testing:       { label: "BETA TESTING",       color: "#E0A526" },
    maintenance:        { label: "MAINTENANCE",        color: "#9FA8D4" },
    emergency:          { label: "EMERGENCY NOTICE",   color: "#FF3B30" },
  };
  const meta = MODE_META[mode] || MODE_META.active_development;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-md p-4"
      data-testid="archive-status-notice"
      role="dialog"
      aria-modal="true"
      aria-labelledby="archive-status-notice-title"
    >
      {/* Crosshair / scanline backdrop accents */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.08]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, transparent 0 2px, rgba(255,255,255,0.4) 2px 3px)",
        }}
      />

      <div
        className="relative w-full max-w-2xl border border-[#2D2A26] bg-[#0a0907] shadow-[0_0_60px_rgba(217,45,32,0.18)]"
        data-testid="archive-status-notice-card"
      >
        {/* Top status bar */}
        <div className="flex items-center gap-2 border-b border-[#2D2A26] px-4 py-2">
          <span className="inline-block w-2 h-2 animate-pulse" style={{ background: meta.color }} />
          <span className="font-mono text-[10px] uppercase tracking-[0.35em]" style={{ color: meta.color }}>
            // ARCHIVE STATUS · {meta.label}
          </span>
          <button
            type="button"
            onClick={acknowledge}
            data-testid="archive-status-notice-close"
            aria-label="Dismiss notice"
            className="ml-auto inline-flex items-center justify-center w-6 h-6 text-[#A39E93] hover:text-[#D92D20] border border-transparent hover:border-[#D92D20]"
          >
            <X size={12} />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-6 sm:px-8 sm:py-8">
          <div className="flex items-start gap-3 mb-4">
            <ShieldAlert size={28} className="text-[#E0A526] shrink-0 mt-1" />
            <div>
              <h2
                id="archive-status-notice-title"
                className="font-display text-2xl sm:text-3xl text-[#E4DFD3] leading-tight"
              >
                ARCHIVE STATUS // {meta.label}
              </h2>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39E93] mt-1">
                // a living research platform
              </p>
            </div>
          </div>

          <div className="space-y-3 text-sm text-[#CBB892] leading-relaxed">
            <p>
              The Archive is a living research platform. New investigations,
              evidence, sources, and features are added regularly.
            </p>
            <p>
              Some dossiers are still being expanded, verified, and refined as
              additional source material becomes available.
            </p>
            <p>
              Our editorial process prioritizes accuracy over speed. Information
              may be updated, corrected, or expanded as new evidence is reviewed.
            </p>
            <p>
              If you discover an issue or have a credible source to contribute,
              you can submit it through The Archive.
            </p>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <a
              href="/editorial-standards"
              data-testid="archive-status-notice-standards"
              className="inline-flex items-center justify-center border border-[#454038] hover:border-[#E0A526] text-[#A39E93] hover:text-[#E0A526] px-4 py-2 font-mono text-[10px] uppercase tracking-[0.3em]"
            >
              Editorial Standards
            </a>
            <button
              type="button"
              onClick={acknowledge}
              data-testid="archive-status-notice-ack"
              className="inline-flex items-center justify-center gap-2 bg-[#D92D20] hover:bg-[#B92318] text-white px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.3em]"
            >
              Enter the Archive
            </button>
          </div>
          <div className="mt-3 font-mono text-[9px] uppercase tracking-[0.3em] text-[#5a544a]">
            this notice will not appear again for 7 days
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArchiveStatusNotice;
